import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 72. SEED / DEFAULT DATA
// Prévoir des données initiales : pays · devises · langues · catégories
// principales · rôles standards · permissions · statuts.
// Ces données rendent le socle utilisable dès la première seconde pour
// n’importe quelle entreprise.
// ─────────────────────────────────────────────────────────────

export interface CoreSeedEntry {
  id: number;
  seed_key: string;
  category: string | null;
  label: string | null;
  description: string | null;
  status: string | null;
  item_count: number | null;
  version: string | null;
  applied_at: string | null;
}

export const SEED_STATUS_META: Record<string, { label: string; className: string }> = {
  pending: { label: 'À appliquer', className: 'bg-background-200/70 text-foreground-600' },
  applied: { label: 'Appliqué', className: 'bg-secondary-100 text-secondary-900' },
  failed: { label: 'Échec', className: 'bg-red-100 text-red-800' },
};

export function seedStatusMeta(key: string | null | undefined) {
  return SEED_STATUS_META[key || 'pending'] || SEED_STATUS_META.pending;
}

/** Catalogue de référence des données par défaut du socle. */
export const SEED_CATALOG: { key: string; category: string; label: string; icon: string; desc: string }[] = [
  { key: 'countries', category: 'Géographie', label: 'Pays', icon: 'ri-earth-line', desc: 'Liste des pays et zones géographiques.' },
  { key: 'currencies', category: 'Finance', label: 'Devises', icon: 'ri-money-dollar-circle-line', desc: 'Devises et paramètres monétaires.' },
  { key: 'languages', category: 'Localisation', label: 'Langues', icon: 'ri-translate-2', desc: 'Langues et locales disponibles.' },
  { key: 'main_categories', category: 'Catalogue', label: 'Catégories principales', icon: 'ri-price-tag-3-line', desc: 'Catégories de référence par secteur.' },
  { key: 'standard_roles', category: 'Sécurité', label: 'Rôles standards', icon: 'ri-user-settings-line', desc: 'Owner, Admin, Manager, Staff, Viewer.' },
  { key: 'permissions', category: 'Sécurité', label: 'Permissions', icon: 'ri-shield-keyhole-line', desc: 'Permissions du socle réutilisables.' },
  { key: 'statuses', category: 'Workflow', label: 'Statuts', icon: 'ri-flow-chart', desc: 'Statuts standards (commande, facture, réservation).' },
];

export function seedCatalogMeta(key: string | null | undefined) {
  return SEED_CATALOG.find((s) => s.key === key) || SEED_CATALOG[0];
}

export async function listSeedRegistry(): Promise<CoreSeedEntry[]> {
  const { data, error } = await supabase
    .from('core_seed_registry')
    .select('*')
    .order('category', { ascending: true })
    .order('seed_key', { ascending: true });
  if (error) throw error;
  const rows = (data || []) as CoreSeedEntry[];
  const byKey = new Map<string, CoreSeedEntry>();
  rows.forEach((r) => byKey.set(r.seed_key, r));
  SEED_CATALOG.forEach((c) => {
    if (!byKey.has(c.key)) {
      byKey.set(c.key, {
        id: -1, seed_key: c.key, category: c.category, label: c.label, description: c.desc,
        status: 'pending', item_count: 0, version: '1.0', applied_at: null,
      });
    }
  });
  return Array.from(byKey.values());
}

/** Marque un jeu de données par défaut comme appliqué (avec compteur). */
export async function applySeed(seedKey: string, itemCount: number): Promise<void> {
  const meta = seedCatalogMeta(seedKey);
  const existing = await supabase.from('core_seed_registry').select('id').eq('seed_key', seedKey).maybeSingle();
  const payload = {
    seed_key: seedKey,
    category: meta.category,
    label: meta.label,
    description: meta.desc,
    status: 'applied',
    item_count: itemCount,
    version: '1.0',
    applied_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  if (existing.data?.id) {
    const { error } = await supabase.from('core_seed_registry').update(payload).eq('id', existing.data.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('core_seed_registry').insert(payload);
    if (error) throw error;
  }
}

export interface SeedStats {
  total: number;
  applied: number;
  pending: number;
  items: number;
}

export async function fetchSeedStats(): Promise<SeedStats> {
  const entries = await listSeedRegistry();
  return {
    total: entries.length,
    applied: entries.filter((e) => e.status === 'applied').length,
    pending: entries.filter((e) => !e.status || e.status === 'pending').length,
    items: entries.reduce((acc, e) => acc + (e.item_count || 0), 0),
  };
}